import moment from "moment";
import React from "react";
import DataType from "components/utils/DataType";
import { isLocaleDE, isLocaleEN, isLocaleNL } from "utils/locales";
import { intl } from "LocaleWrapper";
import { DateUtils, FormattedDate } from "utils/DateUtils";
import { Language } from "imports/Utils";
import { faExclamationCircle } from "@fortawesome/pro-regular-svg-icons";
import { Tooltip } from "@mui/material";
import { Tag, TagSize } from "@cofano/cck";
import { getStoredRegionLocale } from "utils/regionUtils";
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

/**
 * A collection of BOS data format functions.
 * This could be used to have consistent formatting of data.
 */

const DataFormat = {

	/**
	 * Determine the most relevant timestamp for the arrival time of a call with label
	 * @param {object} [call]
	 * @returns {ReactNode}
	 */
	callArrivalTime: (call) => {
		if (!call) {
			return <span>{intl.formatMessage({ id: "UNKNOWN" })}</span>;
		}
		let time, label;
		if (call.ata) {
			time = call.ata;
			label = "CALLS_ATA";
		} else if (call.systemAta) {
			time = call.systemAta;
			label = "CALLS_SYSTEM_ATA";
		} else if (call.eta) {
			time = call.eta;
			label = "CALLS_ETA";
		} else if (call.systemEta) {
			time = call.systemEta;
			label = "CALLS_SYSTEM_ETA";
		} else if (call.pta) {
			time = call.pta;
			label = "CALLS_PTA";
		}
		if (!time) {
			return <span>{intl.formatMessage({ id: "UNKNOWN" })}</span>;
		}
		return (
			<span>
				<FormattedDate date={time} withoutYear />
				<small>({intl.formatMessage({ id: label })})</small>
			</span>
		);
	},

	/**
	 * Determine the most relevant timestamp for the departure time of a call with label
	 * @param {object} [call]
	 * @returns {ReactNode}
	 */
	callDepartureTime: (call) => {
		if (!call) {
			return <span>{intl.formatMessage({ id: "UNKNOWN" })}</span>;
		}
		let time, label;
		if (call.atd) {
			time = call.atd;
			label = "CALLS_ATD";
		} else if (call.systemAtd) {
			time = call.systemAtd;
			label = "CALLS_SYSTEM_ATD";
		} else if (call.etd) {
			time = call.etd;
			label = "CALLS_ETD";
		} else if (call.systemEtd) {
			time = call.systemEtd;
			label = "CALLS_SYSTEM_ETD";
		} else if (call.ptd) {
			time = call.ptd;
			label = "CALLS_PTD";
		}
		if (!time) {
			return <span>{intl.formatMessage({ id: "UNKNOWN" })}</span>;
		}
		return (
			<span>
				<FormattedDate date={time} withoutYear />
				<small>({intl.formatMessage({ id: label })})</small>
			</span>
		);
	},

	/**
	 * Format a price base on system default currency settings
	 * Those can be specified in language keys `LANGUAGE_CURRENCY` and `LANGUAGE_COMMA_POINT`
	 *
	 * @example
	 * € 32,98
	 * $ 32.98
	 * € 99,-
	 *
	 * @param {number|null|undefined} price - price as normal decimal! Do **NOT** use big decimal!
	 * @param {string | null | undefined} [currency] - isoCode of currency, default "EUR"
	 * @param {string} [locale] - locale from session(e.g. EN/NL/DE)
	 * @returns {string|undefined}
	 */
	price: (price, currency = "EUR", locale) => {
		if (!DataType.isValidNumber(price)) {
			return undefined;
		}

		const resolvedLocale = locale || getStoredRegionLocale();
		const formattedNumber = new Intl.NumberFormat(resolvedLocale, {
			minimumFractionDigits: 2,
			maximumFractionDigits: 2,
		}).format(price);
		const currencySign = DataFormat.currencySign(currency, resolvedLocale);

		return `${currencySign} ${formattedNumber}`;
	},

	currencySign: (currency, locale) => {
		return new Intl.NumberFormat(locale || getStoredRegionLocale(), {
			style: "currency",
			currency,
			currencyDisplay: "narrowSymbol",
		}).formatToParts(0).find(part => part.type === "currency")?.value || currency;
	},

	/**
	 * Transform big decimal before evaluating {@link DataFormat.price}
	 *
	 * @see DataFormat.price
	 *
	 * @param {number} value - price as big decimal
	 * @param {number} [currency] - isoCode of currency, default "EUR"
	 * @param {string} [locale] - locale from session(e.g. EN/NL/DE)
	 * @returns {React.Fragment|undefined}
	 */
	priceFromBigDecimal: (value, currency, locale) =>
		DataFormat.price(DataType.fromBigDecimal(value), currency, locale),

	/**
	 *	Appends a unit to a value
	 *
	 *	@param {number|null|undefined} [value] - number value
	 *	@param {string} unit - unit of type DataFormat.UNITS
	 *  @returns {string}
	 */
	simpleUnit: (value, unit) => `${new Intl.NumberFormat(getStoredRegionLocale()).format(value ?? 0)} ${unit}`,

	/**
	 * Formats a percentage
	 *
	 * @example
	 * 13.23 %
	 * -0.65 %
	 *
	 * @param {number|undefined|null} value - percentage (where 100 = 100% on 1 = 1%) to be formatted (0.1 will NOT be 10%)
	 * @param decimals - the amount of decimals to should be rounded
	 * @param {boolean|undefined} withSign - whether to always show the sign ( '+' / '-' )
	 * @returns {string|undefined}
	 */
	percentage: (value, decimals = 2, withSign = false) => {
		const sign = withSign && value > 0 ? "+" : "";

		return DataType.isValidNumber(value)
			? `${sign}${new Intl.NumberFormat(getStoredRegionLocale(), { minimumFractionDigits: decimals, maximumFractionDigits: decimals }).format(value)} %`
			: undefined;
	},

	/**
	 * return isoCode or displayCode from containerGroupDTO or else undefined
	 *
	 * @param {Object} obj - containerGroupDTO
	 * @param {string} obj.isoCode - isoCode form containerGroupDTO
	 * @param {string} obj.displayCode - displayCode form containerGroupDTO
	 * @returns {string|undefined}
	 */
	containerGroup: ({ isoCode, displayCode } = {}) => {
		return isoCode || displayCode;
	},

	/**
	 * return isoTypeCode or displayCode from containerTypeDTO or else undefined
	 *
	 * @param {Object} obj - containerTypeDTO
	 * @param {string} obj.isoTypeCode - isoTypeCode form containerTypeDTO
	 * @param {string} obj.displayCode - displayCode form containerTypeDTO
	 * @returns {string|undefined}
	 */
	containerType: (type) => {
		return type?.isoTypeCode || type?.displayCode || "";
	},

	/**
	 * return container empty or full text
	 *
	 * @param {boolean}  - true or false if container is empty
	 * @returns {string}
	 */
	containerIsEmpty: isEmpty => {
		return intl.formatMessage({ id: isEmpty ? "CARGO_EMPTY" : "CARGO_FULL" });
	},

	/**
	 * Format a number from minutes to hours
	 *
	 * @example
	 * 60 => 1 hour
	 * 180 => 3 hours
	 *
	 * @param {number|null|undefined} value in minutes
	 * @returns {string|undefined} value in hours
	 */
	minutesToHours: (value) => {
		if (!DataType.isValidNumber(value)) {
			return undefined;
		}

		return `${DataFormat.rounded(value / 60)} ${intl.formatMessage({ id: "HOURS" })}`;
	},

	/**
	 * Round a number to the given precision
	 *
	 * @param {number} n number to round
	 * @param {number} precision precision to round to (default `2`)
	 * @returns {number|undefined} rounded number or undefined if `n` is not a number
	 */
	rounded: (n, precision = 2) => {
		if (!DataType.isValidNumber(n)) {
			return undefined;
		}

		return Math.round((n + Number.EPSILON) * 10 ** precision) / (10 ** precision);
	},

	/**
	 * Format a number to time string
	 *
	 * @example
	 * 65 => 1 uur 5 minuten
	 * 3608 => 2 dagen 12 uur 8 minuten
	 *
	 * @param {number|null|undefined} value in minutes
	 * @returns {string|undefined}
	 */
	minutesToTime: value => {
		if (!DataType.isValidNumber(value)) {
			return undefined;
		}

		const { days, hours, minutes } = DateUtils.convertMinutesToTimeUnits(value);

		const dayString =
			days > 0
				? days < 2
					? `${days} ${intl.formatMessage({ id: "DAY" })}`
					: `${days} ${intl.formatMessage({ id: "DAYS" })}`
				: "";
		const hourString =
			hours > 0 ?
				hours < 2
					?  `${hours} ${intl.formatMessage({ id: "HOUR" })}`
					:  `${hours} ${intl.formatMessage({ id: "HOURS" })}`
				: "";
		const minuteString =
			minutes > 0
				? minutes < 2
					? `${minutes} ${intl.formatMessage({ id: "MINUTE" })}`
					: `${minutes} ${intl.formatMessage({ id: "MINUTES" })}`
				: "";

		return `${dayString} ${hourString} ${minuteString}`;
	},

	/**
	 * Formats a boolean as check mark or cross
	 *
	 * @param {string|boolean|null|undefined} boolean
	 * @return {string|undefined} either a check mark or a cross
	 */
	boolean: boolean =>
		DataType.isValidBoolean(boolean) ? (
			DataType.asBoolean(boolean) ? (
				<CheckIcon sx={{ display: "flex", fontSize: 16 }} />
			) : (
				<CloseIcon sx={{ display: "flex", fontSize: 16 }} />
			)
		) : undefined,

	/**
	 * return yes or no
	 *
	 * @param {boolean|null|undefined} boolean - true or false
	 * @returns {string}
	 */
	booleanAsText: boolean => {
		return intl.formatMessage({ id: boolean ? "YES" : "NO" });
	},

	/**
	 * Display the correct description based on the given current session locale
	 *
	 * @param locale - current session locale
	 * @param descriptionNL
	 * @param descriptionEN
	 * @param descriptionDE
	 * @return {undefined|string}
	 */
	adnDescription: (locale, { descriptionNL, descriptionEN, descriptionDE }) => {
		if (!DataType.isValidString(locale)) {
			return undefined;
		}

		if (isLocaleNL(locale)) {
			return descriptionNL;
		} else if (isLocaleEN(locale)) {
			return descriptionEN;
		} else if (isLocaleDE(locale)) {
			return descriptionDE;
		}
	},

	adnProperShippingName: (locale, { properShippingNameNL, properShippingNameEN, properShippingNameDE, properShippingNameFR }) => {
		if (!DataType.isValidString(locale)) {
			return undefined;
		}

		if (isLocaleNL(locale)) {
			return properShippingNameNL;
		} else if (isLocaleEN(locale)) {
			return properShippingNameEN;
		} else if (isLocaleDE(locale)) {
			return properShippingNameDE;
		}
	},

	adnProperShippingNameOptional: (locale, adnGood) => {
		if (!DataType.isValidString(locale)) {
			return undefined;
		}

		if (isLocaleNL(locale)) {
			return adnGood.properShippingNameNL || adnGood.descriptionNL;
		} else if (isLocaleEN(locale)) {
			return adnGood.properShippingNameEN || adnGood.descriptionEN;
		} else if (isLocaleDE(locale)) {
			return adnGood.properShippingNameDE || adnGood.descriptionDE;
		}
	},

	/**
	 * This will truncate long strings and wrap it with a tooltip which include full text
	 *
	 * @param string - given string that should be shortened
	 * @param maxCharacters - amount of characters that should be displayed
	 * @return {ReactNode}
	 */
	stringShortened: (string, maxCharacters = 50) => {
		if (DataType.isValidString(string)) {
			return string.length <= maxCharacters ? (
				string
			) : (
				<Tooltip title={string} placement="top">
					<span>{string.substring(0, maxCharacters) + "..."}</span>
				</Tooltip>
			);
		}
	},

	/**
	 * checks if the given number is valid, adds m³ to the end and returns it as a string.
	 * @example
	 * 10 m³
	 *
	 * @param {number|null|undefined} number
	 * @returns {string|undefined}
	 */
	volumeM3: number => {
		if (!DataType.isValidNumber(number)) {
			return undefined;
		}

		return `${DataFormat.number(number)} m³`;
	},

	/**
	 * checks if the given number is valid, adds m³/h to the end and returns it as a string.
	 * @example
	 * 10 m³/h
	 *
	 * @param {number|null|undefined} number
	 * @returns {string|undefined}
	 */
	volumePerHourM3: number => {
		if (!DataType.isValidNumber(number)) {
			return undefined;
		}

		return `${DataFormat.number(number)} m³/h`;
	},

	/**
	 * checks if the given number is valid, adds m³ to the end and returns it as a string.
	 * @example
	 * 10 mton
	 *
	 * @param {number | string | null | undefined} number
	 * @returns {string|undefined}
	 */
	volumeTon: number => {
		if (!DataType.isValidNumber(number)) {
			return undefined;
		}

		return `${new Intl.NumberFormat(getStoredRegionLocale(), { minimumFractionDigits: 3, maximumFractionDigits: 3 }).format(number)} ${intl.formatMessage({ id: "UNIT_MTON" })}`;
	},

	/**
	 * checks if the given number is valid, adds kg to the end and returns it as a string.
	 * @example
	 * 100 kg
	 *
	 * @param {number|null|undefined} number
	 * @returns {string|undefined}
	 */
	volumeKg: number => {
		if (!DataType.isValidNumber(number)) {
			return undefined;
		}

		return `${DataFormat.number(number)} kg`;
	},

	/**
	 * checks if the given number is valid, adds L15 to the end and returns it as a string.
	 * @example
	 * 10 L15
	 *
	 * @param {number|null|undefined} number
	 * @returns {string|undefined}
	 */
	volumeL15: number => {
		if (!DataType.isValidNumber(number)) {
			return undefined;
		}

		return `${DataFormat.number(number)} L15`;
	},

	/**
	 * checks if the given number is valid, adds L to the end and returns it as a string.
	 * @example
	 * 10 L
	 *
	 * @param {number|null|undefined} number
	 * @returns {string|undefined}
	 */
	volumeL: number => {
		if (!DataType.isValidNumber(number)) {
			return undefined;
		}

		return `${DataFormat.number(number)} L`;
	},

	/**
	 * checks if the given number is valid, adds mton to the end and returns it as a string.
	 * @example
	 * 10 mton
	 *
	 * @param {number|null|undefined} number
	 * @returns {string|undefined}
	 */
	tonnage: number => {
		if (!DataType.isValidNumber(number)) {
			return undefined;
		}

		return `${DataFormat.number(number)} ${intl.formatMessage({ id: "UNIT_MTON" })}`;
	},

	/**
	 * Formats a given number according to the locale settings retrieved from the stored region locale.
	 *
	 * @param {number|bigint} number - The number to be formatted.
	 * @returns {string} The formatted number as a string based on the locale.
	 */
	number: number => new Intl.NumberFormat(getStoredRegionLocale()).format(number),

	/**
	 * checks if the given number is valid, adds the given weight unit to the end and returns it as a string.
	 * @example
	 * 10 mton
	 *
	 * @param {number|null|undefined} number
	 * @param {string|null|undefined} unit
	 * @returns {string|undefined}
	 */
	mtonWeightWithUnit: (number, unit) => {
		if (!DataType.isValidNumber(number)) {
			return undefined;
		}

		if (unit && (unit.toUpperCase() === "KG_AIR" || unit.toUpperCase() === "KG_VACUUM")) {
			number = number * 1000;
		}

		return `${DataFormat.number(number)} ${intl.formatMessage({ id: `UNIT_${unit}` })}`;
	},

	volumeWithUnit: (number, unit) => {
		if (!DataType.isValidNumber(number)) {
			return undefined;
		}

		return `${DataFormat.number(number)} ${intl.formatMessage({ id: `UNIT_${unit}` })}`;
	},

	/**
	 * checks if the given number is valid, adds degrees to the end and returns it as a string.
	 * @example
	 * +107 °C
	 *
	 * @param {number|null|undefined} number
	 * @returns {string|undefined}
	 */
	temperature: number => {
		if (!DataType.isValidNumber(number)) {
			return undefined;
		}

		return `${number > 0 ? "+" : ""}${number} \u00b0C`;
	},

	/**
	 * Return a valid number with TEU unit
	 *
	 * @param {number|null|undefined} number - teu as normal decimal
	 * @returns {string}
	 */
	teu: number => {
		if (!DataType.isValidNumber(number)) {
			return undefined;
		} else {
			return `${DataFormat.number(number)} teu`;
		}
	},

	/**
	 * checks if the given number is valid, adds km/h to the end and returns it as a string.
	 * @example
	 * 10 km/h
	 *
	 * @param {number|null|undefined} number
	 * @returns {string|undefined}
	 */
	kmH: number => {
		if (!DataType.isValidNumber(number)) {
			return undefined;
		}

		return `${DataFormat.number(number)} km/h`;
	},

	/**
	 * checks if the given number is valid, adds ltr/h to the end and returns it as a string.
	 * @example
	 * 10 ltr/h
	 *
	 * @param {number|null|undefined} number
	 * @returns {string|undefined}
	 */
	ltrH: number => {
		if (!DataType.isValidNumber(number)) {
			return undefined;
		}

		return `${DataFormat.number(number)} ltr/h`;
	},

	/**
	 * checks if the given number is valid, adds ltr/km to the end and returns it as a string.
	 * @example
	 * 10 ltr/km
	 *
	 * @param {number|null|undefined} number
	 * @returns {string|undefined}
	 */
	ltrKm: number => {
		if (!DataType.isValidNumber(number)) {
			return undefined;
		}

		return `${DataFormat.number(number)} ltr/km`;
	},

	/**
	 * checks if the given number is valid, adds m to the end and returns it as a string.
	 * @example
	 * 10 m
	 *
	 * @param {number|null|undefined} number
	 * @returns {string|undefined}
	 */
	meter: number => {
		if (!DataType.isValidNumber(number)) {
			return undefined;
		}

		return `${DataFormat.number(number)} m`;
	},

	/**
	 * checks if the given number is valid, adds minute or minutes to the end and returns it as a string.
	 * @example
	 * 10 minutes
	 * 1 minute
	 *
	 * @param {number} number
	 * @returns {string|undefined}
	 */
	minutes: minutes => {
		if (!DataType.isValidNumber(minutes)) {
			return undefined;
		}

		return `${minutes} ${minutes === 1 ? intl.formatMessage({ id: "MINUTE" }) : intl.formatMessage({ id: "MINUTES" })}`;
	},

	/**
	 * checks if contact is valid, return string with contact fields
	 * @example
	 * John Doe (0612345678)
	 *
	 *
	 * @param {Object} obj - contact object from the ship
	 * @param {string} obj.name - name from the contact object
	 * @param {string} obj.insertion - insertion from the contact object
	 * @param {string} obj.surname - surname from the contact object
	 * @param {string} obj.telephone - telephone from the contact object
	 * @returns {string|undefined}
	 */
	shipperContact: contact => {
		if (!contact) {
			return undefined;
		}

		return `${contact.name} ${contact.insertion || ""} ${contact.surName ||
			""} ${contact.telephone ? "(" + contact.telephone + ")" : ""}`;
	},

	/**
	 * checks if the given number is valid, return barge type language as string
	 * @example
	 * Containerschip
	 *
	 *
	 * @param {number} number
	 * @returns {string|undefined}
	 */
	shipBargeType: number => {
		if (!DataType.isValidNumber(number)) {
			return undefined;
		}

		return intl.formatMessage({ id: `BARGE_TYPE_${number}` });
	},

	/**
	 * checks if ship is valid, return string with ship dimension fields
	 * @example
	 * 5 x 5 x 5 (m)
	 *
	 *
	 * @param {ship} - ship object
	 * @returns {string|undefined}
	 */
	shipDimensions: ({ lengthM, widthM, maxDraughtM } = {}) => {
		return `${lengthM || "0"} x ${widthM || "0"} x ${maxDraughtM || "0"}  (m)`;
	},

	/**
	 * Returns the SHIP_AVAILABLEDAYS language key based on the value
	 *
	 * @param {string} workdays
	 * @returns {string}
	 */
	shipAvailableDays: workdays => {
		const number = Number(workdays);
		if (number !== 1 && number !== 2 && number !== 3) {
			return Language("UNKNOWN");
		}
		return Language("SHIP_AVAILABLEDAYS_" + number);
	},

	/**
	 * checks if the given number is valid rounde decimal, return value as string
	 * @example
	 * 47.9 km
	 *
	 *
	 * @param {number|null|undefined} number
	 * @returns {string|undefined}
	 */
	distance: number => {
		if (!DataType.roundDecimal(number)) {
			return undefined;
		}

		return `${DataType.roundDecimal(number)} km`;
	},

	/**
	 * Converts the user enum value to the language value
	 * @example
	 * MAIN -> Hoofdgebruiker
	 *
	 *
	 * @param {string|null|undefined} type
	 * @returns {string}
	 */
	userType: type => {
		return type
			? type === "MAIN"
				? `${intl.formatMessage({ id: "COMPANY_NAME" })}`
				: type === "MAIN_USER_LIMITED"
					? `${intl.formatMessage({ id: "COMPANY_NAME" })} (${intl.formatMessage({ id: "LIMITED" })})`
					: `${intl.formatMessage({ id: `USER_TYPE_${type}` })}` : `${intl.formatMessage({ id: "UNKNOWN" })}`;

	},

	/**
	 * @param {{ name: string, color: string, languageKeyPrefix: string }} status
	 * @return {Tag}
	 */
	contractStatus: status => {
		if (!status) {
			return Language("UNKNOWN");
		}

		return (
			<Tag
				color={status.color}
				text={intl.formatMessage({ id: `${status.languageKeyPrefix}${status.name}` })}
				size={TagSize.SMALL}
			/>
		);
	},

	/**
	 * Returns the difference between @param date and now in days/hours using `moment.fromNow()`. A tooltip is included that shows the
	 * actual datetime value
	 *
	 * @param date unix timestamp in milliseconds
	 * @return {JSX.Element|undefined}
	 */
	timeSinceDate: (date) => {
		if (!date) {
			return undefined;
		}
		return (
			<Tooltip title={<FormattedDate date={date} withoutYear />} placement="top">
				<span>
					{moment.unix(date / 1000).fromNow()}
				</span>
			</Tooltip>
		);
	},

	/**
	 * Show the status description with translation
	 *
	 * @param {Object|undefined} status - status from statusDTO (graphql)
	 * @param {String|undefined} language - selected language from the user (NL, DE, EN)
	 */
	status: (status, language) => {
		const langCode = language?.split('-')[0].toLowerCase() ?? "en";

		const descriptions = status?.translations?.descriptions;
		const defaultDescription = status?.translations?.defaultValue || status?.description || intl.formatMessage({ id: "UNKNOWN" });

		if (Array.isArray(descriptions)) {
			const description = descriptions.find(it => it.key === langCode);
			return description?.value || defaultDescription;
		} else if (typeof descriptions === "object") {
			const description = status?.translations?.descriptions?.[langCode];
			return description || defaultDescription;
		} else {
			return defaultDescription;
		}
	},

	waterLevel: waterLevel => DataFormat.simpleUnit(waterLevel, DataFormat.UNITS.CENTIMETER),

	waterLevelWithMeasuringPoint: (waterLevel, measuringPoint) => DataFormat.simpleUnit(DataFormat.waterLevel(waterLevel), "- " + measuringPoint),

	limitValue: limitType => {
		switch (limitType) {
			case "DEW_POINT":
				return DataFormat.UNITS.CELSIUS;
			case "OXYGEN_LEVEL_PERCENTAGE":
				return DataFormat.UNITS.OXYGEN_LEVEL_VOLUME;
			case "OXYGEN_LEVEL_PPM":
				return DataFormat.UNITS.OXYGEN_LEVEL_PPM;
			default:
				return null;
		}
	},

	UNITS: {
		get MTON() {
			return intl.formatMessage({ id: "UNIT_MTON" });
		},
		KILOGRAM: "kg",
		M3: "m³",
		M3H: "m³/h",
		MH: "m/h",
		CENTIMETER: "cm",
		FEET: "ft.",
		L15: "L15",
		L20: "L20",
		METER: "m",
		KMH: "km/h",
		TEU: "teu",
		TEUH: "teu/h",
		MOVH: "mov/h",
		CELSIUS: "°C",
		DENSITY: "kg/L",
		LITER: "L",
		OXYGEN_LEVEL_PPM: "ppm",
		OXYGEN_LEVEL_VOLUME: "vol.-%",
	},

	/**
	 * This will return the language key according to the angler lock status.
	 * @param {string} status - status of angler lock
	 * @returns {string|undefined}
	 */
	anglerLockStatus: status => {
		switch (status) {
			case "ENTERING_DOWNSTREAM":
				return Language("ANGLER_LOCK_ENTERING_DOWNSTREAM");
			case "EXITING_DOWNSTREAM":
				return Language("ANGLER_LOCK_EXITING_DOWNSTREAM");
			case "ENTERING_UPSTREAM":
				return Language("ANGLER_LOCK_ENTERING_UPSTREAM");
			case "EXITING_UPSTREAM":
				return Language("ANGLER_LOCK_EXITING_UPSTREAM");
			case "LOCKING_DOWNSTREAM":
				return Language("ANGLER_LOCK_LOCKING_DOWNSTREAM");
			case "LOCKING_UPSTREAM":
				return Language("ANGLER_LOCK_LOCKING_UPSTREAM");
			case "PREPARING_UPSTREAM":
				return Language("ANGLER_LOCK_PREPARING_UPSTREAM");
			case "PREPARING_DOWNSTREAM":
				return Language("ANGLER_LOCK_PREPARING_DOWNSTREAM");
			case "BLOCKED":
				return Language("ANGLER_LOCK_BLOCKED");
			case "FREE_PASSAGE":
				return Language("ANGLER_LOCK_FREE_PASSAGE");
			case "OPERATION_STANDBY":
				return Language("ANGLER_LOCK_OPERATION_STANDBY");
			case "UNKNOWN":
				return Language("ANGLER_LOCK_UNKNOWN");
			default:
				return undefined;
		}
	},

	/**
	 * Display the fullname of a contact
	 *
	 * @param {Object} obj - Contact
	 */
	contactName: (contact = {}) => {
		let fullname;
		if (DataType.isValidString(contact.name) && DataType.isValidString(contact.surName)) {
			fullname = `${contact.name} ${contact.insertion ? `${contact.insertion} ` : ""}${contact.surName}`;
		} else if (DataType.isValidString(contact.surName)) {
			fullname = contact.surName;
		} else if (DataType.isValidString(contact.name)) {
			fullname = contact.name;
		} else {
			return undefined;
		}
		return (
			<span>
				{fullname}
				{contact.deletedOn && (
					<Tooltip
						title={(
							<React.Fragment>
								{`${intl.formatMessage({ id: "CONTACT_IS_DELETED_ON" })} `}
								<FormattedDate date={contact.deletedOn} />
							</React.Fragment>
						)}
						placement="top">
						<span>
							<FontAwesomeIcon
								icon={faExclamationCircle}
								color="#ea9457" />
						</span>
					</Tooltip>
				)}
			</span>
		);
	},

	labelTypes: (enumValue) => {
		switch (enumValue) {
			case "COMMENT":
				return Language("COMMENT");
			case "MESSAGE":
				return Language("NOTIFICATION_TYPE_MESSAGE");
			case "CONTRACT":
				return Language("CONTRACT");
			default:
				return Language("UNKNOWN");
		}
	},

	availabilityDays: (availability) => {
		if (availability?.availableMonday && !availability?.availableSaturday && !availability?.availableSunday) {
			return Language("SHIP_AVAILABLEDAYS_1");
		} else if (availability?.availableMonday && !availability?.availableSunday) {
			return Language("SHIP_AVAILABLEDAYS_2");
		} else if (availability?.availableMonday) {
			return Language("SHIP_AVAILABLEDAYS_3");
		} else {
			return "-";
		}
	},
	shipAvailableDayNight: (availability) => {
		if (availability?.availableDayNight === "DAY_AND_NIGHT") {
			return Language("SHIP_AVAILABLEDAYNIGHT_1");
		} else if (availability?.availableDayNight === "DAY") {
			return Language("SHIP_AVAILABLEDAYNIGHT_2");
		} else if (availability?.availableDayNight === "NIGHT") {
			return Language("SHIP_AVAILABLEDAYNIGHT_3");
		} else {
			return "-";
		}
	},


	/**
	 * A wrapper for Remarks field
	 *
	 * @param {String}
	 * @param {null|undefined|string}
	 * @returns {React.element | string}
	 */
	remarks: (remark, fallback) => {
		return (
			<div style={{ whiteSpace: "pre-wrap", wordBreak: "break-all" }}>
				{remark || fallback}
			</div>
		);
	},

	/**
	 * Show the specified costType in the correct locale, with English as fallback
	 *
	 * @param costTypes The costTypes
	 * @param locale{string} The locale
	 * @returns {string}
	 */
	costType: (costTypes, locale) => {
		return costTypes?.[`title_${locale.toLowerCase()}`] || costTypes?.['title_en'] || '-';
	},


	/**
	 * Show the color from a entity
	 *
	 * @param color
	 * @returns {React.element}
	 */
	color: (color) => {
		return (
			<span
				style={{
					backgroundColor: color || "#FFF",
					border: "1px solid #000",
					display: "flex",
					height: 20,
					width: 75,
				}} />
		);
	},


	/**
	 * checks if the given number is valid, adds ft to the end and returns it as a string.
	 * @example
	 * 10 ft
	 *
	 * @param {number|null|undefined} number
	 * @returns {string|undefined}
	 */
	feet: number => {
		if (!DataType.isValidNumber(number)) {
			return undefined;
		}

		return `${DataFormat.number(number)} ft`;
	},
};

export default DataFormat;
